import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const supplierSchema = z.object({
  display_name: z.string().min(1, "Display name is required"),
  email: z.string().email().optional().or(z.literal("")),
  tax_id: z.string().optional(),
  payment_terms: z.number().min(0),
  contact_name: z.string().optional(),
  contact_email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  business_registration_number: z.string().optional(),
  billing_address: z.string().optional(),
  bank_account_number: z.string().optional(),
  bank_account_name: z.string().optional(),
  bank_account_details: z.string().optional(),
  unit_tracking: z.string().optional()
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface SupplierFormProps {
  supplier?: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  open,
  onOpenChange,
  onSuccess
}) => {
  const { toast } = useToast();
  const isEditing = !!supplier;

  const form = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      display_name: supplier?.display_name || '',
      email: supplier?.email || '',
      tax_id: supplier?.tax_id || '',
      payment_terms: supplier?.payment_terms || 30,
      contact_name: supplier?.contact_name || '',
      contact_email: supplier?.contact_email || '',
      phone: supplier?.phone || '',
      business_registration_number: supplier?.business_registration_number || '',
      billing_address: supplier?.billing_address || '',
      bank_account_number: supplier?.bank_account_number || '',
      bank_account_name: supplier?.bank_account_name || '',
      bank_account_details: supplier?.bank_account_details || '',
      unit_tracking: supplier?.unit_tracking || ''
    }
  });

  // Reset form when supplier changes
  useEffect(() => {
    if (supplier) {
      form.reset({
        display_name: supplier.display_name || '',
        email: supplier.email || '',
        tax_id: supplier.tax_id || '',
        payment_terms: supplier.payment_terms || 30,
        contact_name: supplier.contact_name || '',
        contact_email: supplier.contact_email || '',
        phone: supplier.phone || '',
        business_registration_number: supplier.business_registration_number || '',
        billing_address: supplier.billing_address || '',
        bank_account_number: supplier.bank_account_number || '',
        bank_account_name: supplier.bank_account_name || '',
        bank_account_details: supplier.bank_account_details || '',
        unit_tracking: supplier.unit_tracking || ''
      });
    } else {
      form.reset({
        display_name: '',
        email: '',
        tax_id: '',
        payment_terms: 30,
        contact_name: '',
        contact_email: '',
        phone: '',
        business_registration_number: '',
        billing_address: '',
        bank_account_number: '',
        bank_account_name: '',
        bank_account_details: '',
        unit_tracking: ''
      });
    }
  }, [supplier, form]);

  const onSubmit = async (data: SupplierFormData) => {
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('suppliers')
          .update(data)
          .eq('id', supplier.id);
        
        if (error) throw error;
        toast({ title: "Supplier updated successfully" });
      } else {
        const { error } = await supabase
          .from('suppliers')
          .insert([data]);
        
        if (error) throw error;
        toast({ title: "Supplier created successfully" });
      }
      
      onSuccess?.();
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Supplier' : 'New Supplier'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                {...form.register('display_name')}
                placeholder="Enter supplier name"
              />
              {form.formState.errors.display_name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.display_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...form.register('email')}
                placeholder="supplier@company.com"
              />
              {form.formState.errors.email && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tax_id">Tax ID</Label>
              <Input
                id="tax_id"
                {...form.register('tax_id')}
                placeholder="Tax identification number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_terms">Payment Terms (days)</Label>
              <Input
                id="payment_terms"
                type="number"
                {...form.register('payment_terms', { valueAsNumber: true })}
                min="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                {...form.register('contact_name')}
                placeholder="Contact person name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                {...form.register('contact_email')}
                placeholder="contact@supplier.com"
              />
              {form.formState.errors.contact_email && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.contact_email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                {...form.register('phone')}
                placeholder="+30 210 1234567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_registration_number">Business Registration Number</Label>
              <Input
                id="business_registration_number"
                {...form.register('business_registration_number')}
                placeholder="Registration number"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="billing_address">Billing Address</Label>
              <Textarea
                id="billing_address"
                {...form.register('billing_address')}
                placeholder="Full billing address"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_account_number">Bank Account Number</Label>
              <Input
                id="bank_account_number"
                {...form.register('bank_account_number')}
                placeholder="Account number or IBAN"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank_account_name">Bank Account Name</Label>
              <Input
                id="bank_account_name"
                {...form.register('bank_account_name')}
                placeholder="Account holder name"
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="bank_account_details">Bank Account Details</Label>
              <Textarea
                id="bank_account_details"
                {...form.register('bank_account_details')}
                placeholder="SWIFT, branch, etc."
                rows={2}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="unit_tracking">Unit Tracking</Label>
              <Input
                id="unit_tracking"
                {...form.register('unit_tracking')}
                placeholder="Unit tracking information"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};